/**
 * Identity helpers for voice tool / UI activity rows.
 *
 * Map the two legitimate id spellings onto one key: dotted registry
 * (`ui.open_artifact`) and OpenAI-sanitized (`ui_open_artifact`).
 * Deliberately nothing else — no lowercasing, no whitespace folding —
 * so an English title can never alias an id.
 */

export function canonicalToolId(name: string): string {
  return name.replace(/[.\-]+/g, '_');
}

/**
 * Registry / sanitized tool names that back a voice ``ui`` frame action.
 * Used to skip a redundant ``kind: 'ui'`` timeline row when the tool lifecycle
 * already covers the same action.
 *
 * Voice registry uses ``ui.show_toast`` (see ml-scribe REALTIME_UI_TOOL_NAMES).
 * The agent-events bus uses ``ui.toast`` — different surface, intentionally not
 * listed here.
 */
export function toolKeysForUiAction(action: string): string[] {
  switch (action) {
    case 'navigate':
      return ['ui.navigate', 'ui_navigate'];
    case 'open_artifact':
      return ['ui.open_artifact', 'ui_open_artifact'];
    case 'toast':
      return ['ui.show_toast', 'ui_show_toast'];
    case 'highlight':
      return ['ui.highlight', 'ui_highlight'];
    case 'open_template_picker':
      return ['ui.open_template_picker', 'ui_open_template_picker'];
    case 'start_guide':
      return ['help.start_guide', 'help_start_guide'];
    default:
      return [];
  }
}

/** Only match a concurrent / just-finished tool call, not the whole session. */
export const UI_TOOL_MATCH_WINDOW_MS = 5_000;

export function hasToolForUiAction(
  action: string,
  tools: ReadonlyArray<{ name: string; status?: string; ts?: number }>,
  now = Date.now()
): boolean {
  const keys = new Set(toolKeysForUiAction(action).map(canonicalToolId));
  if (keys.size === 0) return false;
  return tools.some((t) => {
    if (!keys.has(canonicalToolId(t.name))) return false;
    if (t.status === 'running') return true;
    if (typeof t.ts !== 'number') return false;
    return now - t.ts <= UI_TOOL_MATCH_WINDOW_MS;
  });
}
